import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { estoqueApi, humanApi } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Package,
  Warehouse,
  ArrowRightLeft,
  FileText,
  Plus,
  Search,
  Edit2,
  Trash2,
  Camera,
  Upload,
  X,
  AlertTriangle,
  CheckCircle,
  Loader2,
  MapPin,
  User,
  BarChart3,
  Eye,
  Info,
  FileWarning,
  TrendingDown,
  Box,
  ArrowDownToLine,
  ArrowUpFromLine,
  ClipboardCheck,
  CalendarDays,
  Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const EstoquePage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'diretoria'].includes(user?.role);
  
  const [activeTab, setActiveTab] = useState(isAdmin ? 'saldos' : 'saida');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [produtos, setProdutos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  
  // Filters
  const [buscaProduto, setBuscaProduto] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroLocal, setFiltroLocal] = useState('');
  
  // Modals
  const [showProdutoModal, setShowProdutoModal] = useState(false);
  const [showLocalModal, setShowLocalModal] = useState(false);
  const [showSaidaModal, setShowSaidaModal] = useState(false);
  const [showNfeModal, setShowNfeModal] = useState(false);
  const [showEntradaManualModal, setShowEntradaManualModal] = useState(false);
  const [showReplicarLocalModal, setShowReplicarLocalModal] = useState(false);
  
  // Edit states
  const [editingProduto, setEditingProduto] = useState(null);
  const [editingLocal, setEditingLocal] = useState(null);
  const [replicarLocal, setReplicarLocal] = useState(null);
  const [replicarQuantidade, setReplicarQuantidade] = useState(1);
  
  // Form states
  const [produtoForm, setProdutoForm] = useState({
    codigo: '', nome: '', descricao: '', unidade: 'UN', categoria: '', estoque_minimo: ''
  });
  const [localForm, setLocalForm] = useState({
    nome: '', tipo: 'central', responsavel_id: '', responsavel_nome: '', endereco: '', ativo: true
  });
  const [saidaForm, setSaidaForm] = useState({
    produto_id: '', quantidade: '', local_origem_id: '', motivo: '', destino_referencia: '', observacao: '', foto_url: ''
  });
  const [entradaForm, setEntradaForm] = useState({
    produto_id: '', quantidade: '', local_destino_id: '', observacao: ''
  });
  const [entradaSimilar, setEntradaSimilar] = useState(null);
  const [verificandoSimilar, setVerificandoSimilar] = useState(false);
  
  // NF-e states
  const [nfeXml, setNfeXml] = useState('');
  const [nfeParsed, setNfeParsed] = useState(null);
  const [nfeLocalDestino, setNfeLocalDestino] = useState('');
  const [nfeItensParaImportar, setNfeItensParaImportar] = useState([]);
  const [nfeOrigem, setNfeOrigem] = useState(null);
  
  // Photo capture
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Inventário states
  const [inventarios, setInventarios] = useState([]);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [inventarioLocal, setInventarioLocal] = useState('');
  const [inventarioData, setInventarioData] = useState(new Date().toISOString().split('T')[0]);
  const [inventarioItens, setInventarioItens] = useState([]);
  const [inventarioObs, setInventarioObs] = useState('');
  const [saldosLocal, setSaldosLocal] = useState(null);
  const [loadingSaldos, setLoadingSaldos] = useState(false);
  const [inventarioDetalhes, setInventarioDetalhes] = useState(null);
  const [showInventarioDetalhes, setShowInventarioDetalhes] = useState(false);
  const fileInputRef = useRef(null);
  
  // Stats
  const [stats, setStats] = useState({
    totalProdutos: 0,
    alertasBaixoEstoque: 0,
    totalLocais: 0,
    movimentacoesHoje: 0
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [produtosRes, locaisRes, categoriasRes] = await Promise.all([
        estoqueApi.listarProdutos(),
        estoqueApi.listarLocais(),
        estoqueApi.listarCategorias(),
      ]);
      
      setProdutos(produtosRes.data);
      setLocais(locaisRes.data);
      setCategorias(categoriasRes.data);
      
      try {
        const humanasRes = await humanApi.list();
        setFuncionarios(humanasRes.data.filter(h => h.status === 'ativo'));
      } catch (e) {
        console.log('Erro ao carregar funcionarios');
      }
      
      if (isAdmin) {
        try {
          const saldosRes = await estoqueApi.listarSaldos();
          setSaldos(saldosRes.data);
          
          const alertas = saldosRes.data.filter(s => s.alerta_baixo).length;
          setStats(prev => ({
            ...prev,
            totalProdutos: produtosRes.data.length,
            alertasBaixoEstoque: alertas,
            totalLocais: locaisRes.data.length
          }));
        } catch (e) {
          console.log('Saldos nao disponiveis');
        }
        
        const movsRes = await estoqueApi.listarMovimentacoes({ limit: 50 });
        setMovimentacoes(movsRes.data);
        
        const hoje = new Date().toISOString().split('T')[0];
        const movsHoje = movsRes.data.filter(m => m.created_at?.startsWith(hoje)).length;
        setStats(prev => ({ ...prev, movimentacoesHoje: movsHoje }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do estoque');
    } finally {
      setLoading(false);
    }
  };
  
  // ===== PRODUTOS =====
  const handleSaveProduto = async () => {
    if (!produtoForm.codigo || !produtoForm.nome) {
      toast.error('Codigo e nome sao obrigatorios');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        ...produtoForm,
        estoque_minimo: produtoForm.estoque_minimo ? parseFloat(produtoForm.estoque_minimo) : null
      };
      
      if (editingProduto) {
        await estoqueApi.atualizarProduto(editingProduto.id, data);
        toast.success('Produto atualizado');
      } else {
        await estoqueApi.criarProduto(data);
        toast.success('Produto criado');
      }
      
      setShowProdutoModal(false);
      setEditingProduto(null);
      setProdutoForm({ codigo: '', nome: '', descricao: '', unidade: 'UN', categoria: '', estoque_minimo: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteProduto = async (produto) => {
    if (!window.confirm(`Excluir produto "${produto.nome}"?`)) return;
    
    try {
      await estoqueApi.excluirProduto(produto.id);
      toast.success('Produto excluido');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir produto');
    }
  };
  
  const openEditProduto = (produto) => {
    setEditingProduto(produto);
    setProdutoForm({
      codigo: produto.codigo,
      nome: produto.nome,
      descricao: produto.descricao || '',
      unidade: produto.unidade,
      categoria: produto.categoria || '',
      estoque_minimo: produto.estoque_minimo?.toString() || ''
    });
    setShowProdutoModal(true);
  };
  
  // ===== LOCAIS =====
  const handleSaveLocal = async () => {
    if (!localForm.nome || !localForm.tipo) {
      toast.error('Nome e tipo sao obrigatorios');
      return;
    }
    
    setLoading(true);
    try {
      if (editingLocal) {
        await estoqueApi.atualizarLocal(editingLocal.id, localForm);
        toast.success('Local atualizado');
      } else {
        await estoqueApi.criarLocal(localForm);
        toast.success('Local criado');
      }
      
      setShowLocalModal(false);
      setEditingLocal(null);
      setLocalForm({ nome: '', tipo: 'central', responsavel_id: '', responsavel_nome: '', endereco: '', ativo: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar local');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteLocal = async (local) => {
    if (!window.confirm(`Excluir local "${local.nome}"?`)) return;
    
    try {
      await estoqueApi.excluirLocal(local.id);
      toast.success('Local excluido');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir local');
    }
  };
  
  const handleReplicarLocal = async () => {
    if (!replicarLocal || replicarQuantidade < 1) {
      toast.error('Selecione um local e informe a quantidade');
      return;
    }
    
    setLoading(true);
    try {
      // Extrair número do nome do local base
      const nomeBase = replicarLocal.nome;
      const match = nomeBase.match(/^(.+?)(\d+)$/);
      
      let prefixo = nomeBase;
      let numeroInicial = 1;
      
      if (match) {
        // Se o nome termina com número (ex: "Apartamento 1"), usa esse como base
        prefixo = match[1].trim();
        numeroInicial = parseInt(match[2]) + 1;
      } else {
        // Se não termina com número (ex: "Apartamento"), começa do 2
        prefixo = nomeBase.trim() + ' ';
        numeroInicial = 2;
      }
      
      // Criar os locais replicados
      const promessas = [];
      for (let i = 0; i < replicarQuantidade; i++) {
        const novoNome = `${prefixo}${numeroInicial + i}`;
        const novoLocal = {
          nome: novoNome,
          tipo: replicarLocal.tipo,
          responsavel_id: replicarLocal.responsavel_id || '',
          responsavel_nome: replicarLocal.responsavel_nome || '',
          endereco: replicarLocal.endereco || '',
          ativo: true
        };
        promessas.push(estoqueApi.criarLocal(novoLocal));
      }
      
      await Promise.all(promessas);
      toast.success(`${replicarQuantidade} locais criados com sucesso!`);
      
      setShowReplicarLocalModal(false);
      setReplicarLocal(null);
      setReplicarQuantidade(1);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao replicar locais');
    } finally {
      setLoading(false);
    }
  };
  
  const openEditLocal = (local) => {
    setEditingLocal(local);
    setLocalForm({
      nome: local.nome,
      tipo: local.tipo,
      responsavel_id: local.responsavel_id || '',
      responsavel_nome: local.responsavel_nome || '',
      endereco: local.endereco || '',
      ativo: local.ativo
    });
    setShowLocalModal(true);
  };
  
  // ===== SAIDA COM FOTO =====
  const startCamera = async () => {
    setCapturingPhoto(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Erro ao acessar camera');
      setCapturingPhoto(false);
    }
  };
  
  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setCapturingPhoto(false);
  };
  
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setPhotoPreview(dataUrl);
      stopCamera();
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSaidaSubmit = async () => {
    if (!saidaForm.produto_id || !saidaForm.quantidade || !saidaForm.local_origem_id) {
      toast.error('Produto, quantidade e local sao obrigatorios');
      return;
    }
    
    if (!photoPreview) {
      toast.error('Foto e obrigatoria para registrar saida');
      return;
    }
    
    setLoading(true);
    try {
      const uploadRes = await estoqueApi.uploadFoto(photoPreview);
      const fotoUrl = uploadRes.data.foto_url;
      
      await estoqueApi.criarMovimentacao({
        tipo: 'saida',
        produto_id: saidaForm.produto_id,
        quantidade: parseFloat(saidaForm.quantidade),
        local_origem_id: saidaForm.local_origem_id,
        motivo: saidaForm.motivo,
        destino_referencia: saidaForm.destino_referencia,
        observacao: saidaForm.observacao,
        foto_url: fotoUrl
      });
      
      toast.success('Saida registrada com sucesso!');
      setShowSaidaModal(false);
      setSaidaForm({ produto_id: '', quantidade: '', local_origem_id: '', motivo: '', destino_referencia: '', observacao: '', foto_url: '' });
      setPhotoPreview(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar saida');
    } finally {
      setLoading(false);
    }
  };
  
  // ===== ENTRADA MANUAL =====
  
  // Verificar entrada similar quando produto e quantidade mudam
  const verificarEntradaSimilar = async (produto_id, quantidade) => {
    if (!produto_id || !quantidade || parseFloat(quantidade) <= 0) {
      setEntradaSimilar(null);
      return;
    }
    
    setVerificandoSimilar(true);
    try {
      const res = await estoqueApi.verificarEntradaSimilar(produto_id, parseFloat(quantidade));
      setEntradaSimilar(res.data.tem_similar ? res.data : null);
    } catch (error) {
      console.log('Erro ao verificar similaridade:', error);
      setEntradaSimilar(null);
    } finally {
      setVerificandoSimilar(false);
    }
  };
  
  // Debounce para verificar similaridade
  useEffect(() => {
    const timer = setTimeout(() => {
      if (entradaForm.produto_id && entradaForm.quantidade) {
        verificarEntradaSimilar(entradaForm.produto_id, entradaForm.quantidade);
      } else {
        setEntradaSimilar(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [entradaForm.produto_id, entradaForm.quantidade]);
  
  const handleEntradaManualSubmit = async () => {
    if (!entradaForm.produto_id || !entradaForm.quantidade || !entradaForm.local_destino_id) {
      toast.error('Produto, quantidade e local sao obrigatorios');
      return;
    }
    
    setLoading(true);
    try {
      await estoqueApi.criarMovimentacao({
        tipo: 'entrada',
        produto_id: entradaForm.produto_id,
        quantidade: parseFloat(entradaForm.quantidade),
        local_destino_id: entradaForm.local_destino_id,
        observacao: entradaForm.observacao
      });
      
      toast.success('Entrada registrada!');
      setShowEntradaManualModal(false);
      setEntradaForm({ produto_id: '', quantidade: '', local_destino_id: '', observacao: '' });
      setEntradaSimilar(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar entrada');
    } finally {
      setLoading(false);
    }
  };
  
  // ===== NF-e =====
  const handleNfeFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    const isXml = fileName.endsWith('.xml');
    const isPdf = fileName.endsWith('.pdf');
    
    if (!isXml && !isPdf) {
      toast.error('Selecione um arquivo XML ou PDF');
      return;
    }
    
    setLoading(true);
    
    try {
      if (isXml) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const xml = event.target.result;
          setNfeXml(xml);
          await parseNfeXml(xml);
        };
        reader.readAsText(file);
      } else {
        const formData = new FormData();
        formData.append('arquivo', file);
        
        const res = await estoqueApi.parseNfePdf(formData);
        setNfeParsed(res.data);
        setNfeOrigem('pdf');
        setNfeItensParaImportar(res.data.itens.map(item => ({
          ...item,
          importar: true,
          produto_id: null
        })));
        
        if (res.data.itens.length > 0) {
          toast.success(`PDF processado: ${res.data.itens.length} itens encontrados`, { duration: 5000 });
        } else {
          toast.warning('Nenhum item encontrado no PDF', { duration: 5000 });
        }
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo');
      setNfeParsed(null);
      setLoading(false);
    }
  };
  
  const parseNfeXml = async (xml) => {
    try {
      const res = await estoqueApi.parseNfe(xml);
      setNfeParsed(res.data);
      setNfeOrigem('xml');
      setNfeItensParaImportar(res.data.itens.map(item => ({
        ...item,
        importar: true,
        produto_id: null
      })));
      toast.success(`NF-e ${res.data.numero} processada: ${res.data.itens.length} itens`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar XML');
      setNfeParsed(null);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImportNfe = async () => {
    if (!nfeLocalDestino) {
      toast.error('Selecione o local de destino');
      return;
    }
    
    const itensParaImportar = nfeItensParaImportar.filter(i => i.importar);
    if (itensParaImportar.length === 0) {
      toast.error('Selecione ao menos um item para importar');
      return;
    }
    
    setLoading(true);
    try {
      await estoqueApi.importarNfe({
        local_destino_id: nfeLocalDestino,
        nfe: nfeParsed,
        itens: itensParaImportar
      });
      
      toast.success(`${itensParaImportar.length} itens importados!`);
      resetNfeModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar NF-e');
    } finally {
      setLoading(false);
    }
  };
  
  const resetNfeModal = () => {
    setShowNfeModal(false);
    setNfeParsed(null);
    setNfeXml('');
    setNfeItensParaImportar([]);
    setNfeLocalDestino('');
    setNfeOrigem(null);
  };
  
  // Filtered data
  const produtosFiltrados = produtos.filter(p => {
    if (buscaProduto && !p.nome.toLowerCase().includes(buscaProduto.toLowerCase()) && 
        !p.codigo.toLowerCase().includes(buscaProduto.toLowerCase())) return false;
    if (filtroCategoria && filtroCategoria !== '__all__' && p.categoria !== filtroCategoria) return false;
    return true;
  });
  
  const saldosFiltrados = saldos.filter(s => {
    if (filtroLocal && filtroLocal !== '__all__' && s.local_id !== filtroLocal) return false;
    if (buscaProduto && !s.produto_nome.toLowerCase().includes(buscaProduto.toLowerCase()) &&
        !s.produto_codigo.toLowerCase().includes(buscaProduto.toLowerCase())) return false;
    return true;
  });
  
  const unidades = ['UN', 'KG', 'M', 'M2', 'M3', 'L', 'CX', 'PC', 'PAR', 'ROL', 'SC'];
  const tiposLocal = [
    { value: 'central', label: 'Almoxarifado Central', icon: Warehouse },
    { value: 'externo', label: 'Estoque Externo/Campo', icon: MapPin },
    { value: 'funcionario', label: 'Estoque Funcionario', icon: User },
  ];
  
  // ===== INVENTARIO FUNCTIONS =====
  
  // Carregar inventários quando aba ativa
  useEffect(() => {
    if (activeTab === 'inventario' && isAdmin) {
      loadInventarios();
    }
  }, [activeTab, isAdmin]);
  
  const loadInventarios = async () => {
    try {
      const res = await estoqueApi.listarInventarios({ limit: 20 });
      setInventarios(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar inventarios:', error);
    }
  };
  
  const handleAbrirNovoInventario = () => {
    setInventarioLocal('');
    setInventarioData(new Date().toISOString().split('T')[0]);
    setInventarioItens([]);
    setInventarioObs('');
    setSaldosLocal(null);
    setShowInventarioModal(true);
  };
  
  const handleCarregarSaldosLocal = async () => {
    if (!inventarioLocal) {
      toast.error('Selecione um local');
      return;
    }
    
    setLoadingSaldos(true);
    try {
      const res = await estoqueApi.saldosPorLocal(inventarioLocal, inventarioData);
      setSaldosLocal(res.data);
      
      // Preparar itens para contagem
      const itens = (res.data.saldos || []).map(s => ({
        produto_id: s.produto_id,
        produto_codigo: s.produto_codigo,
        produto_nome: s.produto_nome,
        produto_unidade: s.produto_unidade,
        saldo_esperado: s.saldo,
        quantidade_contada: s.saldo // Inicia com saldo esperado
      }));
      setInventarioItens(itens);
    } catch (error) {
      toast.error('Erro ao carregar saldos do local');
    } finally {
      setLoadingSaldos(false);
    }
  };
  
  const handleAtualizarContagem = (index, quantidade) => {
    const novos = [...inventarioItens];
    novos[index].quantidade_contada = parseFloat(quantidade) || 0;
    setInventarioItens(novos);
  };
  
  const handleSalvarInventario = async () => {
    if (!inventarioLocal || inventarioItens.length === 0) {
      toast.error('Selecione um local e carregue os saldos primeiro');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        local_id: inventarioLocal,
        data_contagem: inventarioData,
        itens: inventarioItens.map(i => ({
          produto_id: i.produto_id,
          quantidade_contada: i.quantidade_contada
        })),
        observacoes: inventarioObs
      };
      
      const res = await estoqueApi.criarInventario(payload);
      toast.success('Inventario registrado com sucesso!');
      setShowInventarioModal(false);
      loadInventarios();
      
      // Mostrar resumo
      const divergentes = res.data.total_itens_divergentes || 0;
      if (divergentes > 0) {
        toast.warning(`Atenção: ${divergentes} item(ns) com divergencia`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar inventario');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerInventario = async (inventarioId) => {
    try {
      const res = await estoqueApi.obterInventario(inventarioId);
      setInventarioDetalhes(res.data);
      setShowInventarioDetalhes(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };
  
  const handleAplicarAjustes = async (inventarioId) => {
    if (!window.confirm('Deseja aplicar os ajustes de estoque baseados nas divergências? Esta ação criará movimentações de entrada/saída para corrigir os saldos.')) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await estoqueApi.aplicarAjustes(inventarioId);
      toast.success(res.data.message);
      loadInventarios();
      loadData();
      if (showInventarioDetalhes) {
        handleVerInventario(inventarioId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aplicar ajustes');
    } finally {
      setLoading(false);
    }
  };

  // Tab config
  const tabs = isAdmin ? [
    { id: 'saldos', label: 'Saldos', icon: BarChart3 },
    { id: 'produtos', label: 'Produtos', icon: Package },
    { id: 'locais', label: 'Locais', icon: Warehouse },
    { id: 'movimentacoes', label: 'Historico', icon: ArrowRightLeft },
    { id: 'inventario', label: 'Inventario', icon: ClipboardCheck },
  ] : [
    { id: 'saida', label: 'Registrar Saida', icon: Camera },
  ];

  return (
    <div className="min-h-screen bg-stone-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-stone-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Controle de Estoque
              </h1>
              <p className="text-stone-500 text-sm mt-1">
                {isAdmin ? 'Gerencie produtos, locais e movimentacoes' : 'Registre retiradas de material'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {isAdmin && (
                <>
                  <Button
                    onClick={() => setShowNfeModal(true)}
                    className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
                    data-testid="btn-importar-nfe"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Importar</span> NF-e
                  </Button>
                  <Button
                    onClick={() => setShowEntradaManualModal(true)}
                    className="bg-stone-800 hover:bg-stone-700 text-stone-100 border border-stone-700"
                    data-testid="btn-entrada-manual"
                  >
                    <ArrowDownToLine className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Entrada</span>
                  </Button>
                </>
              )}
              <Button
                onClick={() => { setShowSaidaModal(true); setPhotoPreview(null); }}
                className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
                data-testid="btn-registrar-saida"
              >
                <ArrowUpFromLine className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Registrar</span> Saida
              </Button>
            </div>
          </div>
          
          {/* Stats Cards (Admin only) */}
          {isAdmin && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-stone-900/80 border border-stone-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Produtos</p>
                    <p className="text-2xl font-semibold text-stone-100 mt-1">{stats.totalProdutos}</p>
                  </div>
                  <Box className="w-8 h-8 text-stone-700" />
                </div>
              </div>
              <div className="bg-stone-900/80 border border-stone-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Locais</p>
                    <p className="text-2xl font-semibold text-stone-100 mt-1">{stats.totalLocais}</p>
                  </div>
                  <Warehouse className="w-8 h-8 text-stone-700" />
                </div>
              </div>
              <div className={cn(
                "border rounded-lg p-4",
                stats.alertasBaixoEstoque > 0 
                  ? "bg-amber-500/10 border-amber-500/20" 
                  : "bg-stone-900/80 border-stone-800"
              )}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Alertas</p>
                    <p className={cn(
                      "text-2xl font-semibold mt-1",
                      stats.alertasBaixoEstoque > 0 ? "text-amber-400" : "text-stone-100"
                    )}>{stats.alertasBaixoEstoque}</p>
                  </div>
                  <TrendingDown className={cn("w-8 h-8", stats.alertasBaixoEstoque > 0 ? "text-amber-500/50" : "text-stone-700")} />
                </div>
              </div>
              <div className="bg-stone-900/80 border border-stone-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Mov. Hoje</p>
                    <p className="text-2xl font-semibold text-stone-100 mt-1">{stats.movimentacoesHoje}</p>
                  </div>
                  <ArrowRightLeft className="w-8 h-8 text-stone-700" />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Tabs */}
        <div className="border-b border-stone-800 mb-6">
          <nav className="flex gap-1 overflow-x-auto pb-px -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2",
                  activeTab === tab.id
                    ? "text-amber-500 border-amber-500 bg-stone-800/50"
                    : "text-stone-400 border-transparent hover:text-stone-200 hover:bg-stone-800/30"
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Content */}
        <div className="space-y-6">
          {/* SALDOS TAB */}
          {activeTab === 'saldos' && isAdmin && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-stone-800">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <h2 className="text-lg font-medium text-stone-200">Saldos por Local</h2>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                      <Input
                        placeholder="Buscar produto..."
                        value={buscaProduto}
                        onChange={(e) => setBuscaProduto(e.target.value)}
                        className="pl-9 bg-stone-950 border-stone-800 text-stone-100 placeholder:text-stone-500 w-full sm:w-48"
                        data-testid="input-busca-saldo"
                      />
                    </div>
                    <Select value={filtroLocal} onValueChange={setFiltroLocal}>
                      <SelectTrigger className="w-full sm:w-40 bg-stone-950 border-stone-800 text-stone-300" data-testid="select-filtro-local">
                        <SelectValue placeholder="Todos locais" />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-900 border-stone-700">
                        <SelectItem value="__all__">Todos locais</SelectItem>
                        {locais.map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead className="bg-stone-900/80 border-b border-stone-800">
                    <tr>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Local</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Codigo</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Produto</th>
                      <th className="text-right px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Qtd</th>
                      <th className="text-center px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saldosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-stone-500 py-12">
                          Nenhum saldo encontrado
                        </td>
                      </tr>
                    ) : (
                      saldosFiltrados.map((s, idx) => (
                        <tr key={`${s.produto_id}-${s.local_id}`} className="border-b border-stone-800/50 hover:bg-stone-800/30 transition-colors">
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-300">{s.local_nome}</td>
                          <td className="px-4 sm:px-6 py-4 font-mono text-sm text-stone-400">{s.produto_codigo}</td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-100 font-medium">{s.produto_nome}</td>
                          <td className="px-4 sm:px-6 py-4 text-right text-sm text-stone-100 font-medium tabular-nums">
                            {s.quantidade} <span className="text-stone-500">{s.produto_unidade}</span>
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-center">
                            {s.alerta_baixo ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                <AlertTriangle className="w-3 h-3" />
                                Baixo
                              </span>
                            ) : s.quantidade > 0 ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                <CheckCircle className="w-3 h-3" />
                                OK
                              </span>
                            ) : (
                              <span className="text-stone-600 text-xs">Zerado</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* PRODUTOS TAB */}
          {activeTab === 'produtos' && isAdmin && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-stone-800">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <h2 className="text-lg font-medium text-stone-200">Produtos Cadastrados</h2>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                      <Input
                        placeholder="Buscar..."
                        value={buscaProduto}
                        onChange={(e) => setBuscaProduto(e.target.value)}
                        className="pl-9 bg-stone-950 border-stone-800 text-stone-100 w-full sm:w-48"
                        data-testid="input-busca-produto"
                      />
                    </div>
                    <Button
                      onClick={() => {
                        setEditingProduto(null);
                        setProdutoForm({ codigo: '', nome: '', descricao: '', unidade: 'UN', categoria: '', estoque_minimo: '' });
                        setShowProdutoModal(true);
                      }}
                      className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
                      data-testid="btn-novo-produto"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Novo Produto
                    </Button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead className="bg-stone-900/80 border-b border-stone-800">
                    <tr>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Codigo</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Nome</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500 hidden sm:table-cell">Unidade</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500 hidden md:table-cell">Categoria</th>
                      <th className="text-right px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500 hidden sm:table-cell">Min.</th>
                      <th className="px-4 sm:px-6 py-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-stone-500 py-12">
                          Nenhum produto encontrado
                        </td>
                      </tr>
                    ) : (
                      produtosFiltrados.map(p => (
                        <tr key={p.id} className="border-b border-stone-800/50 hover:bg-stone-800/30 transition-colors group">
                          <td className="px-4 sm:px-6 py-4 font-mono text-sm text-stone-400">{p.codigo}</td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-100 font-medium">{p.nome}</td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-400 hidden sm:table-cell">{p.unidade}</td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-400 hidden md:table-cell">{p.categoria || '-'}</td>
                          <td className="px-4 sm:px-6 py-4 text-right text-sm text-stone-400 tabular-nums hidden sm:table-cell">{p.estoque_minimo || '-'}</td>
                          <td className="px-4 sm:px-6 py-4">
                            <div className="flex gap-1 justify-end opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditProduto(p)}
                                className="h-8 w-8 p-0 text-stone-400 hover:text-stone-100 hover:bg-stone-800"
                                data-testid={`btn-edit-produto-${p.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteProduto(p)}
                                className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                                data-testid={`btn-delete-produto-${p.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* LOCAIS TAB */}
          {activeTab === 'locais' && isAdmin && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-stone-200">Locais de Estoque</h2>
                <Button
                  onClick={() => {
                    setEditingLocal(null);
                    setLocalForm({ nome: '', tipo: 'central', responsavel_id: '', responsavel_nome: '', endereco: '', ativo: true });
                    setShowLocalModal(true);
                  }}
                  className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
                  data-testid="btn-novo-local"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Local
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {locais.map(local => {
                  const tipoInfo = tiposLocal.find(t => t.value === local.tipo);
                  const IconComponent = tipoInfo?.icon || Warehouse;
                  return (
                    <div 
                      key={local.id} 
                      className={cn(
                        "bg-stone-900/50 border border-stone-800 rounded-lg p-5 transition-colors hover:border-stone-700",
                        !local.ativo && "opacity-50"
                      )}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          local.tipo === 'central' && "bg-blue-500/10 text-blue-400",
                          local.tipo === 'externo' && "bg-amber-500/10 text-amber-400",
                          local.tipo === 'funcionario' && "bg-emerald-500/10 text-emerald-400",
                        )}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setReplicarLocal(local);
                              setReplicarQuantidade(1);
                              setShowReplicarLocalModal(true);
                            }}
                            className="h-8 w-8 p-0 text-stone-400 hover:text-blue-400 hover:bg-blue-500/10"
                            title="Replicar local"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditLocal(local)}
                            className="h-8 w-8 p-0 text-stone-400 hover:text-stone-100 hover:bg-stone-800"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLocal(local)}
                            className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <h3 className="text-stone-100 font-medium mb-1">{local.nome}</h3>
                      <p className="text-stone-500 text-xs mb-2">{tipoInfo?.label}</p>
                      {local.responsavel_nome && (
                        <p className="text-stone-400 text-sm flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" />
                          {local.responsavel_nome}
                        </p>
                      )}
                      {local.endereco && (
                        <p className="text-stone-500 text-xs mt-1">{local.endereco}</p>
                      )}
                    </div>
                  );
                })}
                {locais.length === 0 && (
                  <div className="col-span-full text-center text-stone-500 py-12 bg-stone-900/30 border border-dashed border-stone-800 rounded-lg">
                    Nenhum local cadastrado
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* MOVIMENTACOES TAB */}
          {activeTab === 'movimentacoes' && isAdmin && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-stone-800">
                <h2 className="text-lg font-medium text-stone-200">Historico de Movimentacoes</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <thead className="bg-stone-900/80 border-b border-stone-800">
                    <tr>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Data</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Tipo</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Produto</th>
                      <th className="text-right px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500">Qtd</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500 hidden md:table-cell">Local</th>
                      <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold tracking-wider uppercase text-stone-500 hidden lg:table-cell">Usuario</th>
                      <th className="px-4 sm:px-6 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentacoes.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-stone-500 py-12">
                          Nenhuma movimentacao
                        </td>
                      </tr>
                    ) : (
                      movimentacoes.map(m => (
                        <tr key={m.id} className="border-b border-stone-800/50 hover:bg-stone-800/30 transition-colors">
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-400 tabular-nums">
                            {m.data_movimentacao 
                              ? new Date(m.data_movimentacao).toLocaleDateString('pt-BR')
                              : m.nfe_data 
                                ? m.nfe_data 
                                : new Date(m.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 sm:px-6 py-4">
                            <span className={cn(
                              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border",
                              m.tipo === 'entrada' && "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
                              m.tipo === 'saida' && "bg-rose-500/10 text-rose-400 border-rose-500/20",
                              m.tipo === 'transferencia' && "bg-blue-500/10 text-blue-400 border-blue-500/20",
                            )}>
                              {m.tipo === 'entrada' ? 'Entrada' : m.tipo === 'saida' ? 'Saida' : 'Transf.'}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-100 font-medium">
                            {m.produto_nome || m.produto_id}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-right text-sm text-stone-100 font-medium tabular-nums">
                            {m.quantidade}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-400 hidden md:table-cell">
                            {m.tipo === 'entrada' && m.local_destino_nome}
                            {m.tipo === 'saida' && m.local_origem_nome}
                            {m.tipo === 'transferencia' && `${m.local_origem_nome} → ${m.local_destino_nome}`}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-stone-500 hidden lg:table-cell">
                            {m.created_by_name}
                          </td>
                          <td className="px-4 sm:px-6 py-4">
                            {m.foto_url && (
                              <a 
                                href={`${process.env.REACT_APP_BACKEND_URL}${m.foto_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-amber-400 hover:text-amber-300 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* INVENTARIO TAB */}
          {activeTab === 'inventario' && isAdmin && (
            <div className="space-y-6">
              {/* Header do Inventário */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-medium text-stone-200">Inventario Fisico</h2>
                  <p className="text-stone-500 text-sm">Registre contagens e compare com o saldo esperado</p>
                </div>
                <Button onClick={handleAbrirNovoInventario} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-novo-inventario">
                  <Plus className="w-4 h-4 mr-2" /> Nova Contagem
                </Button>
              </div>
              
              {/* Lista de Inventários */}
              <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-800 hover:bg-transparent">
                      <TableHead className="text-stone-400">Data</TableHead>
                      <TableHead className="text-stone-400">Local</TableHead>
                      <TableHead className="text-stone-400 text-center">Itens</TableHead>
                      <TableHead className="text-stone-400 text-center">Divergencias</TableHead>
                      <TableHead className="text-stone-400">Status</TableHead>
                      <TableHead className="text-stone-400">Responsavel</TableHead>
                      <TableHead className="text-stone-400 text-right">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventarios.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-stone-500">
                          <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          Nenhum inventario registrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      inventarios.map(inv => (
                        <TableRow key={inv.id} className="border-stone-800">
                          <TableCell className="text-stone-300 font-mono">
                            {inv.data_contagem?.split('-').reverse().join('/')}
                          </TableCell>
                          <TableCell className="text-stone-300">{inv.local_nome}</TableCell>
                          <TableCell className="text-center text-stone-400">{inv.total_itens}</TableCell>
                          <TableCell className="text-center">
                            {inv.total_itens_divergentes > 0 ? (
                              <span className="text-red-400 font-medium">{inv.total_itens_divergentes}</span>
                            ) : (
                              <span className="text-green-400">0</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded text-xs font-medium",
                              inv.status === 'ok' ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                            )}>
                              {inv.status === 'ok' ? 'OK' : 'Divergente'}
                            </span>
                            {inv.ajustes_aplicados && (
                              <span className="ml-2 px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                                Ajustado
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-stone-400 text-sm">{inv.created_by_name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleVerInventario(inv.id)}
                                className="h-8 w-8 text-stone-400 hover:text-stone-100"
                                data-testid={`btn-ver-inv-${inv.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {inv.status === 'divergente' && !inv.ajustes_aplicados && (
                                <Button
                                  size="sm"
                                  onClick={() => handleAplicarAjustes(inv.id)}
                                  className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-xs"
                                >
                                  Aplicar Ajustes
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          
          {/* SAIDA TAB (Non-admin) */}
          {activeTab === 'saida' && !isAdmin && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Camera className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-xl font-medium text-stone-100 mb-2">Registrar Retirada</h3>
              <p className="text-stone-400 mb-6 max-w-md mx-auto">
                Selecione o produto, informe a quantidade e tire uma foto como comprovante
              </p>
              <Button
                onClick={() => { setShowSaidaModal(true); setPhotoPreview(null); }}
                className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold px-6"
              >
                <Camera className="w-4 h-4 mr-2" />
                Iniciar Registro
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Hidden elements */}
      <canvas ref={canvasRef} className="hidden" />
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
      
      {/* ===== MODALS ===== */}
      
      {/* Produto Modal */}
      <Dialog open={showProdutoModal} onOpenChange={setShowProdutoModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-stone-100">{editingProduto ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Codigo *</Label>
                <Input
                  value={produtoForm.codigo}
                  onChange={(e) => setProdutoForm({...produtoForm, codigo: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                  placeholder="EX001"
                  data-testid="input-produto-codigo"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Unidade *</Label>
                <Select value={produtoForm.unidade} onValueChange={(v) => setProdutoForm({...produtoForm, unidade: v})}>
                  <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {unidades.map(u => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Nome *</Label>
              <Input
                value={produtoForm.nome}
                onChange={(e) => setProdutoForm({...produtoForm, nome: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Cimento Portland CP-II"
                data-testid="input-produto-nome"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Descricao</Label>
              <Textarea
                value={produtoForm.descricao}
                onChange={(e) => setProdutoForm({...produtoForm, descricao: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Categoria</Label>
                <Input
                  value={produtoForm.categoria}
                  onChange={(e) => setProdutoForm({...produtoForm, categoria: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  placeholder="Hidraulico"
                  list="categorias-list"
                />
                <datalist id="categorias-list">
                  {categorias.map(c => (<option key={c} value={c} />))}
                </datalist>
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Estoque Min.</Label>
                <Input
                  type="number"
                  value={produtoForm.estoque_minimo}
                  onChange={(e) => setProdutoForm({...produtoForm, estoque_minimo: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  placeholder="10"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowProdutoModal(false)} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button onClick={handleSaveProduto} disabled={loading} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-salvar-produto">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Local Modal */}
      <Dialog open={showLocalModal} onOpenChange={setShowLocalModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-stone-100">{editingLocal ? 'Editar Local' : 'Novo Local'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Nome *</Label>
              <Input
                value={localForm.nome}
                onChange={(e) => setLocalForm({...localForm, nome: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Almoxarifado Central"
                data-testid="input-local-nome"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Tipo *</Label>
              <Select value={localForm.tipo} onValueChange={(v) => setLocalForm({...localForm, tipo: v})}>
                <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-local-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700">
                  {tiposLocal.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {localForm.tipo === 'funcionario' && (
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Responsavel</Label>
                <Select value={localForm.responsavel_id} onValueChange={(v) => {
                  const func = funcionarios.find(f => f.id === v);
                  setLocalForm({...localForm, responsavel_id: v, responsavel_nome: func?.nome || ''});
                }}>
                  <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {funcionarios.map(f => (<SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Endereco</Label>
              <Input
                value={localForm.endereco}
                onChange={(e) => setLocalForm({...localForm, endereco: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Rua das Flores, 123"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowLocalModal(false)} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button onClick={handleSaveLocal} disabled={loading} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-salvar-local">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Replicar Local Modal */}
      <Dialog open={showReplicarLocalModal} onOpenChange={setShowReplicarLocalModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-stone-100">Replicar Local</DialogTitle>
            <DialogDescription className="text-stone-400">
              Criar copias do local com numeracao sequencial
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
              <p className="text-stone-400 text-sm mb-1">Local base:</p>
              <p className="text-stone-100 font-medium">{replicarLocal?.nome}</p>
              <p className="text-stone-500 text-xs mt-1">Tipo: {replicarLocal?.tipo}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Quantos locais criar?</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={replicarQuantidade}
                onChange={(e) => setReplicarQuantidade(parseInt(e.target.value) || 1)}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Ex: 10"
              />
              <p className="text-stone-500 text-xs mt-2">
                {replicarLocal?.nome && replicarQuantidade > 0 && (
                  <>
                    Sera criado: {(() => {
                      const nomeBase = replicarLocal.nome;
                      const match = nomeBase.match(/^(.+?)(\d+)$/);
                      let prefixo = nomeBase;
                      let numeroInicial = 2;
                      
                      if (match) {
                        prefixo = match[1].trim();
                        numeroInicial = parseInt(match[2]) + 1;
                      } else {
                        prefixo = nomeBase.trim() + ' ';
                      }
                      
                      if (replicarQuantidade === 1) {
                        return `"${prefixo}${numeroInicial}"`;
                      } else {
                        return `"${prefixo}${numeroInicial}" ate "${prefixo}${numeroInicial + replicarQuantidade - 1}"`;
                      }
                    })()}
                  </>
                )}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowReplicarLocalModal(false)} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button onClick={handleReplicarLocal} disabled={loading} className="bg-blue-500 hover:bg-blue-400 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Replicar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Saida Modal */}
      <Dialog open={showSaidaModal} onOpenChange={(open) => { if (!open) stopCamera(); setShowSaidaModal(open); }}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-stone-100">Registrar Saida</DialogTitle>
            <DialogDescription className="text-stone-400">Foto obrigatoria como comprovante</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 flex-1 overflow-y-auto">
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Produto *</Label>
              <Select value={saidaForm.produto_id} onValueChange={(v) => setSaidaForm({...saidaForm, produto_id: v})}>
                <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-saida-produto">
                  <SelectValue placeholder="Selecione o produto..." />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 max-h-60">
                  {produtos.map(p => (<SelectItem key={p.id} value={p.id}>{p.codigo} - {p.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Quantidade *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={saidaForm.quantidade}
                  onChange={(e) => setSaidaForm({...saidaForm, quantidade: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  placeholder="10"
                  data-testid="input-saida-quantidade"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Local Origem *</Label>
                <Select value={saidaForm.local_origem_id} onValueChange={(v) => setSaidaForm({...saidaForm, local_origem_id: v})}>
                  <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-saida-local">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {locais.map(l => (<SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Motivo</Label>
              <Input
                value={saidaForm.motivo}
                onChange={(e) => setSaidaForm({...saidaForm, motivo: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Ex: uso externo, manutencao..."
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Destino/Referencia</Label>
              <Input
                value={saidaForm.destino_referencia}
                onChange={(e) => setSaidaForm({...saidaForm, destino_referencia: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Ex: Cliente X, Local Y..."
              />
            </div>
            
            {/* Photo Section */}
            <div className={cn(
              "border-2 border-dashed rounded-lg p-4 transition-colors",
              photoPreview ? "border-emerald-500/30 bg-emerald-500/5" : "border-stone-700 hover:border-amber-500/50"
            )}>
              <Label className="text-sm font-medium text-stone-300 mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Foto do Comprovante *
              </Label>
              
              {capturingPhoto ? (
                <div className="space-y-3">
                  <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg bg-black aspect-video object-cover" />
                  <div className="flex gap-2 justify-center">
                    <Button onClick={capturePhoto} className="bg-emerald-500 hover:bg-emerald-400 text-white">
                      <Camera className="w-4 h-4 mr-2" />
                      Capturar
                    </Button>
                    <Button variant="outline" onClick={stopCamera} className="border-stone-700 text-stone-300">
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : photoPreview ? (
                <div className="space-y-3">
                  <img src={photoPreview} alt="Preview" className="w-full rounded-lg" />
                  <Button variant="outline" onClick={() => setPhotoPreview(null)} className="w-full border-stone-700 text-stone-300 hover:bg-stone-800">
                    <X className="w-4 h-4 mr-2" />
                    Remover foto
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={startCamera} className="flex-1 border-stone-700 text-stone-300 hover:bg-stone-800 hover:border-amber-500/50" data-testid="btn-abrir-camera">
                    <Camera className="w-4 h-4 mr-2" />
                    Abrir Camera
                  </Button>
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="flex-1 border-stone-700 text-stone-300 hover:bg-stone-800">
                    <Upload className="w-4 h-4 mr-2" />
                    Escolher Arquivo
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 border-t border-stone-800 pt-4">
            <Button variant="ghost" onClick={() => { stopCamera(); setShowSaidaModal(false); }} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button onClick={handleSaidaSubmit} disabled={loading || !photoPreview} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-confirmar-saida">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Saida'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Entrada Manual Modal */}
      <Dialog open={showEntradaManualModal} onOpenChange={setShowEntradaManualModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-stone-100">Entrada Manual</DialogTitle>
            <DialogDescription className="text-stone-400">Para entradas sem NF-e</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Produto *</Label>
              <Select value={entradaForm.produto_id} onValueChange={(v) => setEntradaForm({...entradaForm, produto_id: v})}>
                <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-entrada-produto">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 max-h-60">
                  {produtos.map(p => (<SelectItem key={p.id} value={p.id}>{p.codigo} - {p.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Quantidade *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={entradaForm.quantidade}
                  onChange={(e) => setEntradaForm({...entradaForm, quantidade: e.target.value})}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  data-testid="input-entrada-quantidade"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Local Destino *</Label>
                <Select value={entradaForm.local_destino_id} onValueChange={(v) => setEntradaForm({...entradaForm, local_destino_id: v})}>
                  <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-entrada-local">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {locais.map(l => (<SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Aviso de entrada similar */}
            {verificandoSimilar && (
              <div className="flex items-center gap-2 text-stone-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Verificando entradas anteriores...
              </div>
            )}
            
            {entradaSimilar && entradaSimilar.tem_similar && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="aviso-entrada-similar">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-amber-300 font-medium text-sm mb-2">
                      Entrada(s) similar(es) encontrada(s)!
                    </p>
                    <p className="text-amber-200/80 text-xs mb-3">
                      Verifique se nao e duplicidade antes de confirmar:
                    </p>
                    <div className="space-y-2">
                      {entradaSimilar.entradas.slice(0, 3).map((entrada, idx) => (
                        <div key={idx} className="bg-stone-900/50 rounded p-2 text-xs">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-stone-300 font-medium">{entrada.quantidade} un</span>
                              {entrada.local_nome && (
                                <span className="text-stone-500"> em {entrada.local_nome}</span>
                              )}
                            </div>
                            <span className="text-stone-500">
                              {entrada.created_at ? new Date(entrada.created_at).toLocaleDateString('pt-BR') : ''}
                            </span>
                          </div>
                          {entrada.nfe_numero && (
                            <p className="text-stone-500 mt-1">NF-e: {entrada.nfe_numero}</p>
                          )}
                          {entrada.observacao && (
                            <p className="text-stone-500 mt-1 truncate">Obs: {entrada.observacao}</p>
                          )}
                          {entrada.created_by_name && (
                            <p className="text-stone-500 mt-1">Por: {entrada.created_by_name}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Observacao</Label>
              <Textarea
                value={entradaForm.observacao}
                onChange={(e) => setEntradaForm({...entradaForm, observacao: e.target.value})}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Ex: Compra direta, doacao..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => { setShowEntradaManualModal(false); setEntradaSimilar(null); }} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button onClick={handleEntradaManualSubmit} disabled={loading} className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold" data-testid="btn-confirmar-entrada">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* NF-e Import Modal */}
      <Dialog open={showNfeModal} onOpenChange={setShowNfeModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-2xl mx-4 sm:mx-auto max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-stone-100">Importar NF-e</DialogTitle>
            <DialogDescription className="text-stone-400">Upload de arquivo XML ou PDF</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {!nfeParsed ? (
              <div className="space-y-4">
                {/* Info box */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-blue-300 font-medium mb-2">XML e o formato recomendado</p>
                      <p className="text-blue-200/80 mb-2">
                        O arquivo XML contem todos os dados estruturados da nota e e mais confiavel.
                      </p>
                      <p className="text-blue-200/80">
                        <strong>Como obter:</strong> Acesse o <a href="https://www.nfe.fazenda.gov.br/portal/consultaRecebidas.aspx" target="_blank" rel="noopener noreferrer" className="underline text-blue-300 hover:text-blue-200">Portal Nacional da NF-e</a> com certificado digital.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Upload zone */}
                <div className="border-2 border-dashed border-stone-700 hover:border-amber-500/50 rounded-lg p-8 text-center transition-colors">
                  <FileText className="w-12 h-12 mx-auto text-stone-600 mb-4" />
                  <p className="text-stone-300 mb-2">
                    Selecione um arquivo <span className="text-emerald-400 font-medium">XML</span> ou <span className="text-amber-400 font-medium">PDF</span>
                  </p>
                  <p className="text-stone-500 text-xs mb-4">
                    PDF: cada fornecedor tem formato diferente, sempre confira os dados
                  </p>
                  <input type="file" accept=".xml,.pdf" onChange={handleNfeFileUpload} className="hidden" id="nfe-upload" />
                  <label htmlFor="nfe-upload">
                    <Button variant="outline" asChild disabled={loading} className="border-stone-700 text-stone-300 hover:bg-stone-800 hover:border-amber-500/50">
                      <span>
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Selecionar Arquivo
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            ) : (
              <>
                {/* PDF warning */}
                {nfeOrigem === 'pdf' && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <div className="flex gap-3">
                      <FileWarning className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-amber-300 font-medium mb-1">Dados extraidos de PDF - Confira!</p>
                        <p className="text-amber-200/80">
                          Cada fornecedor usa formato diferente. Verifique se os dados estao corretos.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* NF-e Header */}
                <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full font-medium",
                      nfeOrigem === 'xml' ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"
                    )}>
                      {nfeOrigem?.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-stone-500">NF-e:</span>
                      <span className="text-stone-100 ml-2">{nfeParsed.numero || '-'}</span>
                    </div>
                    <div>
                      <span className="text-stone-500">Data:</span>
                      <span className="text-stone-100 ml-2">{nfeParsed.data_emissao || '-'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-stone-500">Fornecedor:</span>
                      <span className="text-stone-100 ml-2">{nfeParsed.fornecedor_nome || '-'}</span>
                    </div>
                    <div>
                      <span className="text-stone-500">Valor Total:</span>
                      <span className="text-emerald-400 ml-2 font-medium">
                        R$ {nfeParsed.valor_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Local destino */}
                <div>
                  <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Local de Destino *</Label>
                  <Select value={nfeLocalDestino} onValueChange={setNfeLocalDestino}>
                    <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-nfe-local">
                      <SelectValue placeholder="Onde os materiais serao estocados..." />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-900 border-stone-700">
                      {locais.map(l => (<SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Items */}
                <div>
                  <Label className="text-sm font-medium text-stone-300 mb-2 block">
                    Itens ({nfeItensParaImportar.filter(i => i.importar).length} selecionados)
                  </Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {nfeItensParaImportar.map((item, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                          item.importar 
                            ? "bg-stone-800/50 border-amber-500/30" 
                            : "bg-stone-900/30 border-stone-800 opacity-50"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={item.importar}
                          onChange={(e) => {
                            const updated = [...nfeItensParaImportar];
                            updated[idx].importar = e.target.checked;
                            setNfeItensParaImportar(updated);
                          }}
                          className="w-4 h-4 rounded border-stone-600 bg-stone-800 text-amber-500 focus:ring-amber-500/50"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-stone-100 text-sm truncate">{item.descricao}</p>
                          <p className="text-stone-500 text-xs">
                            Cod: {item.codigo} | {item.quantidade} {item.unidade}
                          </p>
                        </div>
                        <span className="text-emerald-400 text-sm font-medium whitespace-nowrap tabular-nums">
                          R$ {item.valor_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0 border-t border-stone-800 pt-4">
            <Button variant="ghost" onClick={resetNfeModal} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            {nfeParsed && (
              <Button 
                onClick={handleImportNfe}
                disabled={loading || !nfeLocalDestino || nfeItensParaImportar.filter(i => i.importar).length === 0}
                className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
                data-testid="btn-importar-nfe-confirmar"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Importar Itens'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ===== INVENTARIO MODALS ===== */}
      
      {/* Modal Nova Contagem */}
      <Dialog open={showInventarioModal} onOpenChange={setShowInventarioModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-stone-100 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-500" />
              Nova Contagem de Inventario
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Selecione o local e a data, depois informe as quantidades contadas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Selecao de Local e Data */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Local *</Label>
                <Select value={inventarioLocal} onValueChange={setInventarioLocal}>
                  <SelectTrigger className="bg-stone-950 border-stone-800 text-stone-100" data-testid="select-inv-local">
                    <SelectValue placeholder="Selecione o local..." />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {locais.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-300">Data da Contagem *</Label>
                <Input
                  type="date"
                  value={inventarioData}
                  onChange={(e) => setInventarioData(e.target.value)}
                  className="bg-stone-950 border-stone-800 text-stone-100"
                  data-testid="input-inv-data"
                />
              </div>
            </div>
            
            <Button
              onClick={handleCarregarSaldosLocal}
              disabled={!inventarioLocal || loadingSaldos}
              className="w-full bg-stone-800 hover:bg-stone-700 text-stone-100"
              data-testid="btn-carregar-saldos"
            >
              {loadingSaldos ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Carregar Saldos do Local
            </Button>
            
            {/* Lista de Itens para Contagem */}
            {inventarioItens.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-stone-300">Itens para Contagem ({inventarioItens.length})</Label>
                  <span className="text-stone-500 text-xs">
                    Saldo esperado ate {inventarioData.split('-').reverse().join('/')}
                  </span>
                </div>
                
                <div className="border border-stone-800 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-stone-800 bg-stone-950">
                        <TableHead className="text-stone-400">Codigo</TableHead>
                        <TableHead className="text-stone-400">Produto</TableHead>
                        <TableHead className="text-stone-400 text-center">Esperado</TableHead>
                        <TableHead className="text-stone-400 text-center">Contado</TableHead>
                        <TableHead className="text-stone-400 text-center">Diferenca</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inventarioItens.map((item, idx) => {
                        const diferenca = item.quantidade_contada - item.saldo_esperado;
                        return (
                          <TableRow key={item.produto_id} className="border-stone-800">
                            <TableCell className="text-stone-400 font-mono text-xs">{item.produto_codigo}</TableCell>
                            <TableCell className="text-stone-300 text-sm">{item.produto_nome}</TableCell>
                            <TableCell className="text-center text-stone-400">{item.saldo_esperado}</TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                step="0.01"
                                value={item.quantidade_contada}
                                onChange={(e) => handleAtualizarContagem(idx, e.target.value)}
                                className="w-24 bg-stone-950 border-stone-700 text-stone-100 text-center mx-auto"
                              />
                            </TableCell>
                            <TableCell className={cn(
                              "text-center font-medium",
                              Math.abs(diferenca) < 0.01 ? "text-green-400" : diferenca > 0 ? "text-blue-400" : "text-red-400"
                            )}>
                              {diferenca > 0 ? '+' : ''}{diferenca.toFixed(2)}
                              {Math.abs(diferenca) >= 0.01 && (
                                <span className="block text-xs opacity-70">
                                  {diferenca > 0 ? 'sobra' : 'falta'}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-stone-300">Observacoes</Label>
              <Textarea
                value={inventarioObs}
                onChange={(e) => setInventarioObs(e.target.value)}
                className="bg-stone-950 border-stone-800 text-stone-100"
                placeholder="Observacoes sobre a contagem..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInventarioModal(false)} className="text-stone-300">
              Cancelar
            </Button>
            <Button
              onClick={handleSalvarInventario}
              disabled={loading || inventarioItens.length === 0}
              className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
              data-testid="btn-salvar-inventario"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Registrar Inventario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal Detalhes do Inventário */}
      <Dialog open={showInventarioDetalhes} onOpenChange={setShowInventarioDetalhes}>
        <DialogContent className="bg-stone-900 border-stone-700 text-stone-100 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-500" />
              Detalhes do Inventario
            </DialogTitle>
            {inventarioDetalhes && (
              <DialogDescription className="text-stone-400">
                {inventarioDetalhes.local_nome} - {inventarioDetalhes.data_contagem?.split('-').reverse().join('/')}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {inventarioDetalhes && (
            <div className="space-y-6 py-4">
              {/* Resumo */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-stone-950 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-stone-100">{inventarioDetalhes.total_itens}</p>
                  <p className="text-xs text-stone-500">Total Itens</p>
                </div>
                <div className="bg-stone-950 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">{inventarioDetalhes.total_itens_ok}</p>
                  <p className="text-xs text-stone-500">OK</p>
                </div>
                <div className="bg-stone-950 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-400">{inventarioDetalhes.total_itens_divergentes}</p>
                  <p className="text-xs text-stone-500">Divergentes</p>
                </div>
                <div className="bg-stone-950 rounded-lg p-4 text-center">
                  <span className={cn(
                    "px-3 py-1 rounded text-sm font-medium",
                    inventarioDetalhes.status === 'ok' ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                  )}>
                    {inventarioDetalhes.status === 'ok' ? 'OK' : 'Divergente'}
                  </span>
                  {inventarioDetalhes.ajustes_aplicados && (
                    <p className="text-xs text-blue-400 mt-2">Ajustes aplicados</p>
                  )}
                </div>
              </div>
              
              {/* Tabela de Itens */}
              <div className="border border-stone-800 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-800 bg-stone-950">
                      <TableHead className="text-stone-400">Codigo</TableHead>
                      <TableHead className="text-stone-400">Produto</TableHead>
                      <TableHead className="text-stone-400 text-center">Esperado</TableHead>
                      <TableHead className="text-stone-400 text-center">Contado</TableHead>
                      <TableHead className="text-stone-400 text-center">Divergencia</TableHead>
                      <TableHead className="text-stone-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventarioDetalhes.itens?.map((item, idx) => (
                      <TableRow key={idx} className="border-stone-800">
                        <TableCell className="text-stone-400 font-mono text-xs">{item.produto_codigo}</TableCell>
                        <TableCell className="text-stone-300">{item.produto_nome}</TableCell>
                        <TableCell className="text-center text-stone-400">{item.saldo_esperado}</TableCell>
                        <TableCell className="text-center text-stone-300 font-medium">{item.quantidade_contada}</TableCell>
                        <TableCell className={cn(
                          "text-center font-medium",
                          item.status === 'ok' ? "text-stone-400" : item.divergencia > 0 ? "text-blue-400" : "text-red-400"
                        )}>
                          {item.divergencia > 0 ? '+' : ''}{item.divergencia}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "px-2 py-1 rounded text-xs",
                            item.status === 'ok' ? "bg-green-500/20 text-green-400" :
                            item.status === 'sobra' ? "bg-blue-500/20 text-blue-400" : "bg-red-500/20 text-red-400"
                          )}>
                            {item.status === 'ok' ? 'OK' : item.status === 'sobra' ? 'Sobra' : 'Falta'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Observações */}
              {inventarioDetalhes.observacoes && (
                <div>
                  <Label className="text-stone-400">Observacoes</Label>
                  <p className="text-stone-300 bg-stone-950 rounded p-3 mt-1">{inventarioDetalhes.observacoes}</p>
                </div>
              )}
              
              {/* Botão de Ajuste */}
              {inventarioDetalhes.status === 'divergente' && !inventarioDetalhes.ajustes_aplicados && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      <div>
                        <p className="text-amber-300 font-medium">Divergencias encontradas</p>
                        <p className="text-amber-200/70 text-sm">Deseja aplicar ajustes automaticos no estoque?</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleAplicarAjustes(inventarioDetalhes.id)}
                      disabled={loading}
                      className="bg-amber-500 hover:bg-amber-400 text-stone-950"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar Ajustes'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowInventarioDetalhes(false)} className="bg-stone-800 hover:bg-stone-700 text-stone-100">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstoquePage;
